import React, { useState, useEffect, useCallback } from 'react';
import { ContactFormData, DpeInfo } from '../types';
import { verifyListDPE, callHector } from '../api/dpe';

interface Address {
  label: string;
  housenumber: string;
  street: string;
  postcode: string;
  city: string;
}

interface ContactModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: ContactFormData) => void;
  buttonColor?: string;
  sessionData?: any;
  // Props optionnelles pour les interactions API (depuis WidgetButton)
  formData?: ContactFormData;
  setFormData?: React.Dispatch<React.SetStateAction<ContactFormData>>;
  errors?: Partial<Record<keyof ContactFormData, string>>;
  setErrors?: React.Dispatch<React.SetStateAction<Partial<Record<keyof ContactFormData, string>>>>;
  touched?: Partial<Record<keyof ContactFormData, boolean>>;
  setTouched?: React.Dispatch<React.SetStateAction<Partial<Record<keyof ContactFormData, boolean>>>>;
  suggestions?: Address[];
  setSuggestions?: React.Dispatch<React.SetStateAction<Address[]>>;
  showSuggestions?: boolean;
  setShowSuggestions?: React.Dispatch<React.SetStateAction<boolean>>;
  isAddressSelected?: boolean;
  setIsAddressSelected?: React.Dispatch<React.SetStateAction<boolean>>;
  numeroVoie?: string;
  setNumeroVoie?: React.Dispatch<React.SetStateAction<string>>;
  nomRue?: string;
  setNomRue?: React.Dispatch<React.SetStateAction<string>>;
  nomCommune?: string;
  setNomCommune?: React.Dispatch<React.SetStateAction<string>>;
  codePostal?: string;
  setCodePostal?: React.Dispatch<React.SetStateAction<string>>;
  isValidatingAddress?: boolean;
  setIsValidatingAddress?: React.Dispatch<React.SetStateAction<boolean>>;
  addressValidationError?: string;
  setAddressValidationError?: React.Dispatch<React.SetStateAction<string>>;
  dpeList?: DpeInfo[];
  setDpeList?: React.Dispatch<React.SetStateAction<DpeInfo[]>>;
  showDpeSuggestions?: boolean;
  setShowDpeSuggestions?: React.Dispatch<React.SetStateAction<boolean>>;
  isLoadingDpe?: boolean;
  setIsLoadingDpe?: React.Dispatch<React.SetStateAction<boolean>>;
  isSubmitting?: boolean;
  setIsSubmitting?: React.Dispatch<React.SetStateAction<boolean>>;
  showConfirmationModal?: boolean;
  setShowConfirmationModal?: React.Dispatch<React.SetStateAction<boolean>>;
  showDpePopup?: boolean;
  setShowDpePopup?: React.Dispatch<React.SetStateAction<boolean>>;
  pendingSubmission?: boolean;
  setPendingSubmission?: React.Dispatch<React.SetStateAction<boolean>>;
  isDpeSelectionInProgress?: boolean;
  setIsDpeSelectionInProgress?: React.Dispatch<React.SetStateAction<boolean>>;
  currentDpePage?: number;
  setCurrentDpePage?: React.Dispatch<React.SetStateAction<number>>;
  dpeItemsPerPage?: number;
  // Fonctions optionnelles
  validateEmail?: (email: string) => boolean;
  validateTelephone?: (telephone: string) => boolean;
  validateField?: (fieldName: keyof ContactFormData, value: any) => void;
  validateForm?: () => boolean;
  validateAddress?: (address: string) => Promise<boolean>;
  fetchDpeList?: (adresse: string, numeroVoie: string, nomRue: string, nomCommune: string, codePostal: string) => Promise<void>;
  processSubmission?: (numeroDpeOverride?: string) => Promise<void>;
  getCurrentPageDpes?: () => DpeInfo[];
  getTotalPages?: () => number;
  handleDpePageChange?: (page: number) => void;
  resetDpePagination?: () => void;
  getDpeClassColor?: (classe: string) => { bg: string; text: string; border: string };
  clearAllData?: () => void;
  handleAddressSelect?: (address: Address) => Promise<void>;
  handleDpeSelect?: (dpe: DpeInfo) => Promise<void>;
  handleContinueWithoutDpe?: () => Promise<void>;
}

const ContactModal: React.FC<ContactModalProps> = ({ 
  isOpen, 
  onClose, 
  onSubmit, 
  buttonColor = '#1a1a1a', 
  sessionData,
  // Props optionnelles depuis WidgetButton
  formData: externalFormData,
  setFormData: externalSetFormData,
  errors: externalErrors,
  setErrors: externalSetErrors,
  touched: externalTouched,
  setTouched: externalSetTouched,
  suggestions: externalSuggestions,
  setSuggestions: externalSetSuggestions,
  showSuggestions: externalShowSuggestions,
  setShowSuggestions: externalSetShowSuggestions,
  isAddressSelected: externalIsAddressSelected,
  setIsAddressSelected: externalSetIsAddressSelected,
  numeroVoie: externalNumeroVoie,
  setNumeroVoie: externalSetNumeroVoie,
  nomRue: externalNomRue,
  setNomRue: externalSetNomRue,
  nomCommune: externalNomCommune,
  setNomCommune: externalSetNomCommune,
  codePostal: externalCodePostal,
  setCodePostal: externalSetCodePostal,
  isValidatingAddress: externalIsValidatingAddress,
  setIsValidatingAddress: externalSetIsValidatingAddress,
  addressValidationError: externalAddressValidationError,
  setAddressValidationError: externalSetAddressValidationError,
  dpeList: externalDpeList,
  setDpeList: externalSetDpeList,
  showDpeSuggestions: externalShowDpeSuggestions,
  setShowDpeSuggestions: externalSetShowDpeSuggestions,
  isLoadingDpe: externalIsLoadingDpe,
  setIsLoadingDpe: externalSetIsLoadingDpe,
  isSubmitting: externalIsSubmitting,
  setIsSubmitting: externalSetIsSubmitting,
  showConfirmationModal: externalShowConfirmationModal,
  setShowConfirmationModal: externalSetShowConfirmationModal,
  showDpePopup: externalShowDpePopup,
  setShowDpePopup: externalSetShowDpePopup,
  pendingSubmission: externalPendingSubmission,
  setPendingSubmission: externalSetPendingSubmission,
  isDpeSelectionInProgress: externalIsDpeSelectionInProgress,
  setIsDpeSelectionInProgress: externalSetIsDpeSelectionInProgress,
  currentDpePage: externalCurrentDpePage,
  setCurrentDpePage: externalSetCurrentDpePage,
  dpeItemsPerPage: externalDpeItemsPerPage,
  // Fonctions optionnelles
  validateEmail: externalValidateEmail,
  validateTelephone: externalValidateTelephone,
  validateField: externalValidateField,
  validateForm: externalValidateForm,
  validateAddress: externalValidateAddress,
  fetchDpeList: externalFetchDpeList,
  processSubmission: externalProcessSubmission,
  getCurrentPageDpes: externalGetCurrentPageDpes,
  getTotalPages: externalGetTotalPages,
  handleDpePageChange: externalHandleDpePageChange,
  resetDpePagination: externalResetDpePagination,
  getDpeClassColor: externalGetDpeClassColor,
  clearAllData: externalClearAllData,
  handleAddressSelect: externalHandleAddressSelect,
  handleDpeSelect: externalHandleDpeSelect,
  handleContinueWithoutDpe: externalHandleContinueWithoutDpe
}) => {
  // Liste des pays avec codes et drapeaux
  const countries = [
    { code: '+33', name: 'France', flag: '🇫🇷' },
    { code: '+32', name: 'Belgique', flag: '🇧🇪' },
    { code: '+41', name: 'Suisse', flag: '🇨🇭' },
    { code: '+49', name: 'Allemagne', flag: '🇩🇪' },
    { code: '+39', name: 'Italie', flag: '🇮🇹' },
    { code: '+34', name: 'Espagne', flag: '🇪🇸' },
    { code: '+44', name: 'Royaume-Uni', flag: '🇬🇧' },
    { code: '+1', name: 'États-Unis', flag: '🇺🇸' },
    { code: '+1', name: 'Canada', flag: '🇨🇦' },
  ];
  // Utiliser les props externes si disponibles, sinon les états locaux
  const [localFormData, setLocalFormData] = useState<ContactFormData>({
    nom: '',
    prenom: '',
    mail: '',
    adresse: '',
    numeroDpe: '',
    telephone: '',
    paysCode: '+33',
    professionnelsRge: false,
    conditionsGenerales: false
  });

  const formData = externalFormData || localFormData;
  const setFormData = externalSetFormData || setLocalFormData;

  // États pour la validation
  const [localErrors, setLocalErrors] = useState<Partial<Record<keyof ContactFormData, string>>>({});
  const [localTouched, setLocalTouched] = useState<Partial<Record<keyof ContactFormData, boolean>>>({});

  const errors = externalErrors || localErrors;
  const setErrors = externalSetErrors || setLocalErrors;
  const touched = externalTouched || localTouched;
  const setTouched = externalSetTouched || setLocalTouched;

  // États pour l'aide à la saisie d'adresse
  const [localSuggestions, setLocalSuggestions] = useState<Address[]>([]);
  const [localShowSuggestions, setLocalShowSuggestions] = useState(false);
  const [localIsAddressSelected, setLocalIsAddressSelected] = useState(false);

  const suggestions = externalSuggestions || localSuggestions;
  const setSuggestions = externalSetSuggestions || setLocalSuggestions;
  const showSuggestions = externalShowSuggestions || localShowSuggestions;
  const setShowSuggestions = externalSetShowSuggestions || setLocalShowSuggestions;
  const isAddressSelected = externalIsAddressSelected || localIsAddressSelected;
  const setIsAddressSelected = externalSetIsAddressSelected || setLocalIsAddressSelected;
  
  // États pour les éléments détaillés de l'adresse
  const [localNumeroVoie, setLocalNumeroVoie] = useState('');
  const [localNomRue, setLocalNomRue] = useState('');
  const [localNomCommune, setLocalNomCommune] = useState('');
  const [localCodePostal, setLocalCodePostal] = useState('');

  const numeroVoie = externalNumeroVoie || localNumeroVoie;
  const setNumeroVoie = externalSetNumeroVoie || setLocalNumeroVoie;
  const nomRue = externalNomRue || localNomRue;
  const setNomRue = externalSetNomRue || setLocalNomRue;
  const nomCommune = externalNomCommune || localNomCommune;
  const setNomCommune = externalSetNomCommune || setLocalNomCommune;
  const codePostal = externalCodePostal || localCodePostal;
  const setCodePostal = externalSetCodePostal || setLocalCodePostal;
  
  // États pour la validation d'adresse
  const [localIsValidatingAddress, setLocalIsValidatingAddress] = useState(false);
  const [localAddressValidationError, setLocalAddressValidationError] = useState('');

  const isValidatingAddress = externalIsValidatingAddress || localIsValidatingAddress;
  const setIsValidatingAddress = externalSetIsValidatingAddress || setLocalIsValidatingAddress;
  const addressValidationError = externalAddressValidationError || localAddressValidationError;
  const setAddressValidationError = externalSetAddressValidationError || setLocalAddressValidationError;
  
  // États pour les DPE
  const [localDpeList, setLocalDpeList] = useState<DpeInfo[]>([]);
  const [localShowDpeSuggestions, setLocalShowDpeSuggestions] = useState(false);
  const [localIsLoadingDpe, setLocalIsLoadingDpe] = useState(false);

  const dpeList = externalDpeList || localDpeList;
  const setDpeList = externalSetDpeList || setLocalDpeList;
  const showDpeSuggestions = externalShowDpeSuggestions || localShowDpeSuggestions;
  const setShowDpeSuggestions = externalSetShowDpeSuggestions || setLocalShowDpeSuggestions;
  const isLoadingDpe = externalIsLoadingDpe || localIsLoadingDpe;
  const setIsLoadingDpe = externalSetIsLoadingDpe || setLocalIsLoadingDpe;

  // États pour l'appel API et la modal de confirmation
  const [localIsSubmitting, setLocalIsSubmitting] = useState(false);
  const [localShowConfirmationModal, setLocalShowConfirmationModal] = useState(false);

  const isSubmitting = externalIsSubmitting || localIsSubmitting;
  const setIsSubmitting = externalSetIsSubmitting || setLocalIsSubmitting;
  const showConfirmationModal = externalShowConfirmationModal || localShowConfirmationModal;
  const setShowConfirmationModal = externalSetShowConfirmationModal || setLocalShowConfirmationModal;

  // États pour le popup DPE
  const [localShowDpePopup, setLocalShowDpePopup] = useState(false);
  const [localPendingSubmission, setLocalPendingSubmission] = useState(false);
  const [localIsDpeSelectionInProgress, setLocalIsDpeSelectionInProgress] = useState(false);

  const showDpePopup = externalShowDpePopup || localShowDpePopup;
  const setShowDpePopup = externalSetShowDpePopup || setLocalShowDpePopup;
  const pendingSubmission = externalPendingSubmission || localPendingSubmission;
  const setPendingSubmission = externalSetPendingSubmission || setLocalPendingSubmission;
  const isDpeSelectionInProgress = externalIsDpeSelectionInProgress || localIsDpeSelectionInProgress;
  const setIsDpeSelectionInProgress = externalSetIsDpeSelectionInProgress || setLocalIsDpeSelectionInProgress;

  // États pour la pagination DPE
  const [localCurrentDpePage, setLocalCurrentDpePage] = useState(1);
  const [localDpeItemsPerPage] = useState(10);

  const currentDpePage = externalCurrentDpePage || localCurrentDpePage;
  const setCurrentDpePage = externalSetCurrentDpePage || setLocalCurrentDpePage;
  const dpeItemsPerPage = externalDpeItemsPerPage || localDpeItemsPerPage;

  // Fonction pour fermer les suggestions
  const closeSuggestions = useCallback(() => {
    setShowSuggestions(false);
  }, [setShowSuggestions]);

  // Effet pour récupérer les suggestions d'adresse
  useEffect(() => {
    const fetchAddresses = async () => {
      if (formData.adresse.length < 3) {
        setSuggestions([]);
        setShowSuggestions(false);
        return;
      }

      // Ne pas afficher les suggestions si une adresse a été sélectionnée
      if (isAddressSelected) {
        return;
      }

      try {
        const response = await fetch(
          `https://api-adresse.data.gouv.fr/search/?q=${encodeURIComponent(formData.adresse)}&limit=10`
        );
        const data = await response.json();

        const formattedAddresses = data.features.map((feature: any) => {
          console.log('Feature properties:', feature.properties);
          return {
            housenumber: feature.properties.housenumber,
            label: feature.properties.label,
            postcode: feature.properties.postcode,
            city: feature.properties.city,
            street: feature.properties.street
          };
        });

        setSuggestions(formattedAddresses);
        setShowSuggestions(true);
      } catch (error) {
        console.error('Erreur lors de la recherche d\'adresse:', error);
      }
    };

    // Debounce pour éviter trop d'appels API
    const timeoutId = setTimeout(fetchAddresses, 300);
    return () => clearTimeout(timeoutId);
  }, [formData.adresse, isAddressSelected, setSuggestions, setShowSuggestions]);

  // Effet pour fermer les suggestions quand on clique en dehors
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (showSuggestions && !target.closest('.address-suggestions-container')) {
        closeSuggestions();
      }
      if (showDpeSuggestions && !target.closest('.dpe-suggestions-container')) {
        setShowDpeSuggestions(false);
      }
    };

    if (showSuggestions || showDpeSuggestions) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showSuggestions, showDpeSuggestions, closeSuggestions, setShowDpeSuggestions]);

  // Fonctions de validation (utiliser les externes si disponibles)
  const validateEmail = externalValidateEmail || ((email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  });

  const validateTelephone = externalValidateTelephone || ((telephone: string): boolean => {
    const cleanTelephone = telephone.replace(/[^0-9]/g, '');
    const mobileRegex = /^0[67]\d{8}$/;
    const fixeRegex = /^0[1-59]\d{8}$/;
    return mobileRegex.test(cleanTelephone) || fixeRegex.test(cleanTelephone);
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    
    // Pour le téléphone, permettre les espaces et tirets mais les nettoyer pour l'affichage
    let processedValue = value;
    if (name === 'telephone') {
      // Permettre les espaces et tirets pour l'affichage, mais les nettoyer pour la validation
      processedValue = value;
    }
    
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : processedValue
    }));

    // Marquer le champ comme touché
    setTouched(prev => ({
      ...prev,
      [name]: true
    }));

    // Réinitialiser l'état de sélection quand l'utilisateur tape dans le champ adresse
    if (name === 'adresse') {
      setIsAddressSelected(false);
      // Réinitialiser les éléments détaillés de l'adresse
      setNumeroVoie('');
      setNomRue('');
      setNomCommune('');
      setCodePostal('');
      // Réinitialiser les DPE
      setDpeList([]);
      // setFilteredDpeList([]); // Variable supprimée
      setShowDpeSuggestions(false);
    }

    
    // Validation en temps réel
    validateField(name as keyof ContactFormData, type === 'checkbox' ? checked : processedValue);
  };

  // Fonction pour gérer la sélection d'une adresse
  const handleAddressSelect = externalHandleAddressSelect || (async (address: Address) => {
    console.log('Adresse sélectionnée:', address);
    
    setFormData(prev => ({
      ...prev,
      adresse: address.label
    }));
    
    // Remplir les éléments détaillés de l'adresse
    setNumeroVoie(address.housenumber || '');
    setNomRue(address.street || '');
    setNomCommune(address.city || '');
    setCodePostal(address.postcode || '');
    
    setShowSuggestions(false);
    setSuggestions([]);
    setIsAddressSelected(true);
    setTouched(prev => ({
      ...prev,
      adresse: true
    }));
    // Valider le champ adresse après sélection
    validateField('adresse', address.label);
    
    // Récupérer les DPE pour cette adresse
    console.log('Appel fetchDpeList avec:', {
      adresse: address.label,
      numeroVoie: address.housenumber || '',
      nomRue: address.street || '',
      nomCommune: address.city || '',
      codePostal: address.postcode || ''
    });
    
    // Essayer de récupérer les DPE même si certains champs sont manquants
    const numeroVoie = address.housenumber || '';
    const nomRue = address.street || '';
    const nomCommune = address.city || '';
    const codePostal = address.postcode || '';
    
    // Récupérer les DPE en parallèle lors de la sélection d'adresse
    if (address.label && codePostal) {
      // Lancer la recherche des DPE en arrière-plan (sans bloquer l'interface)
      fetchDpeList(address.label, numeroVoie, nomRue, nomCommune, codePostal);
    } else {
      console.log('Adresse ou code postal manquant, impossible de récupérer les DPE');
    }
  });

  // Fonction pour récupérer la liste des DPE
  const fetchDpeList = externalFetchDpeList || (async (adresse: string, numeroVoie: string, nomRue: string, nomCommune: string, codePostal: string) => {
    console.log('fetchDpeList appelée avec:', { adresse, numeroVoie, nomRue, nomCommune, codePostal });
    
    if (!adresse || !nomRue || !nomCommune || !codePostal) {
      console.log('Paramètres manquants, arrêt de fetchDpeList');
      console.log('Paramètres reçus:', {
        adresse: adresse || 'MANQUANT',
        numeroVoie: numeroVoie || 'VIDE (OK)',
        nomRue: nomRue || 'MANQUANT',
        nomCommune: nomCommune || 'MANQUANT',
        codePostal: codePostal || 'MANQUANT'
      });
      return;
    }

    console.log('Début de la récupération des DPE...');
    setIsLoadingDpe(true);
    try {
      console.log('Appel API verifyListDPE...');
      const dpeList = await verifyListDPE(adresse, numeroVoie, nomRue, nomCommune, codePostal);
      console.log('Réponse API DPE:', dpeList);
      setDpeList(dpeList);
      resetDpePagination(); // Réinitialiser la pagination pour les nouveaux DPE
      console.log('DPE récupérés et mis à jour:', dpeList);
    } catch (error) {
      console.error('Erreur lors de la récupération des DPE:', error);
      setDpeList([]);
    } finally {
      setIsLoadingDpe(false);
      console.log('Chargement DPE terminé');
    }
  });

  // Fonction pour traiter la soumission finale
  const processSubmission = useCallback(async (numeroDpeOverride?: string) => {
    setIsSubmitting(true);
    
    try {
      // Formater le téléphone au format international
      // Nettoyer le numéro (enlever espaces, tirets, points)
      const cleanTelephone = formData.telephone.replace(/[\s.-]/g, '');
      
      // Pour la France (+33), remplacer le 0 initial par 33
      let formattedTelephone = cleanTelephone;
      if (cleanTelephone.startsWith('0')) {
        const countryCode = formData.paysCode.substring(1); 
        formattedTelephone = countryCode + cleanTelephone.substring(1);
      } else{
        const countryCode = formData.paysCode.substring(1); 
        formattedTelephone = countryCode + cleanTelephone;
      }
      
      // Ajouter le + au début
      formattedTelephone = '+' + formattedTelephone;
      
      console.log('Téléphone formaté:', formattedTelephone);
      
      // Utiliser le numéro DPE fourni en paramètre ou celui du formData
      const numeroDpeToUse = numeroDpeOverride !== undefined ? numeroDpeOverride : formData.numeroDpe;
      console.log('Numéro DPE utilisé pour la soumission:', numeroDpeToUse);
      
      // Appel API callHector
      const result = await callHector(
        formData.nom,
        formData.prenom,
        formData.mail,
        formData.adresse,
        numeroVoie,
        nomRue,
        nomCommune,
        codePostal,
        numeroDpeToUse,
        formattedTelephone,
        formData.professionnelsRge ? 'Oui' : 'Non',
        formData.conditionsGenerales? 'Oui' : 'Non',
        sessionData?.sessionId || ''
      );
      
      if (result.success) {
        // Effacer toutes les données du formulaire
        clearAllData();
        
        // Ouvrir la modal de confirmation (sans fermer la modal de contact)
        setShowConfirmationModal(true);
      } else {
        // Afficher l'erreur
        alert(`Erreur: ${result.message}`);
      }
    } catch (error) {
      console.error('Erreur lors de l\'appel API:', error);
      alert('Une erreur est survenue lors de l\'envoi de votre demande.');
    } finally {
      setIsSubmitting(false);
      setPendingSubmission(false);
    }
  }, [formData, numeroVoie, nomRue, nomCommune, codePostal, sessionData]); // eslint-disable-line react-hooks/exhaustive-deps

  // Effet pour gérer l'ouverture du popup DPE quand une soumission est en attente
  useEffect(() => {
    if (pendingSubmission && !isLoadingDpe && dpeList.length > 0 && !showDpePopup && !isDpeSelectionInProgress) {
      setShowDpePopup(true);
    } else if (pendingSubmission && !isLoadingDpe && dpeList.length === 0 && !isDpeSelectionInProgress) {
      // Aucun DPE trouvé, procéder directement à la soumission
      console.log('Aucun DPE trouvé, soumission directe sans DPE');
      processSubmission('');
    }
  }, [pendingSubmission, isLoadingDpe, dpeList.length, showDpePopup, isDpeSelectionInProgress, processSubmission, setShowDpePopup]);

  // Fonction pour gérer la sélection d'un DPE
  const handleDpeSelect = externalHandleDpeSelect || (async (dpe: DpeInfo) => {
    console.log('DPE sélectionné:', dpe);
    console.log('Numéro DPE:', dpe.numeroDpe);
    
    // Marquer qu'une sélection DPE est en cours
    setIsDpeSelectionInProgress(true);
    
    // Fermer le popup DPE immédiatement
    setShowDpePopup(false);
    
    setFormData(prev => {
      const newFormData = {
        ...prev,
        numeroDpe: dpe.numeroDpe
      };
      console.log('Nouveau formData:', newFormData);
      return newFormData;
    });
    setTouched(prev => ({
      ...prev,
      numeroDpe: true
    }));
    // Valider le champ DPE après sélection
    validateField('numeroDpe', dpe.numeroDpe);
    
    // Si une soumission est en attente, traiter la soumission
    if (pendingSubmission) {
      console.log('Soumission en attente, traitement avec DPE:', dpe.numeroDpe);
      await processSubmission(dpe.numeroDpe);
    }
  });

  // Fonction pour continuer sans DPE
  const handleContinueWithoutDpe = externalHandleContinueWithoutDpe || (async () => {
    // Marquer qu'une sélection DPE est en cours
    setIsDpeSelectionInProgress(true);
    
    // Fermer le popup DPE immédiatement
    setShowDpePopup(false);
    
    setFormData(prev => ({
      ...prev,
      numeroDpe: ''
    }));
    
    // Si une soumission est en attente, traiter la soumission
    if (pendingSubmission) {
      console.log('Soumission en attente, traitement sans DPE');
      await processSubmission('');
    }
  });

  // Fonction pour obtenir la couleur selon la classe DPE
  const getDpeClassColor = (classe: string) => {
    const classeUpper = classe.toUpperCase();
    switch (classeUpper) {
      case 'A':
        return {
          bg: 'bg-green-800',
          text: 'text-white',
          border: 'border-green-800'
        };
      case 'B':
        return {
          bg: 'bg-green-600',
          text: 'text-white',
          border: 'border-green-600'
        };
      case 'C':
        return {
          bg: 'bg-green-400',
          text: 'text-white',
          border: 'border-green-400'
        };
      case 'D':
        return {
          bg: 'bg-yellow-400',
          text: 'text-gray-900',
          border: 'border-yellow-400'
        };
      case 'E':
        return {
          bg: 'bg-orange-500',
          text: 'text-white',
          border: 'border-orange-500'
        };
      case 'F':
        return {
          bg: 'bg-orange-600',
          text: 'text-white',
          border: 'border-orange-600'
        };
      case 'G':
        return {
          bg: 'bg-red-600',
          text: 'text-white',
          border: 'border-red-600'
        };
      default:
        return {
          bg: 'bg-gray-400',
          text: 'text-white',
          border: 'border-gray-400'
        };
    }
  };

  // Fonctions de pagination DPE
  const getCurrentPageDpes = () => {
    const startIndex = (currentDpePage - 1) * dpeItemsPerPage;
    const endIndex = startIndex + dpeItemsPerPage;
    return dpeList.slice(startIndex, endIndex);
  };

  const getTotalPages = () => {
    return Math.ceil(dpeList.length / dpeItemsPerPage);
  };

  const handleDpePageChange = (page: number) => {
    setCurrentDpePage(page);
  };

  const resetDpePagination = () => {
    setCurrentDpePage(1);
  };

  // Fonction pour nettoyer toutes les données
  const clearAllData = () => {
    console.log('🧹 Nettoyage de toutes les données...');
    
    // Réinitialiser le formulaire
    setFormData({
      nom: '',
      prenom: '',
      mail: '',
      adresse: '',
      numeroDpe: '',
      telephone: '',
      paysCode: '+33',
      professionnelsRge: false,
      conditionsGenerales: false
    });
    
    // Réinitialiser les états d'adresse
    setNumeroVoie('');
    setNomRue('');
    setNomCommune('');
    setCodePostal('');
    setSuggestions([]);
    setShowSuggestions(false);
    setIsAddressSelected(false);
    setAddressValidationError('');
    setIsValidatingAddress(false);
    
    // Réinitialiser les états de validation
    setTouched({});
    setErrors({});
    
    // Réinitialiser les états DPE
    setDpeList([]);
    // setFilteredDpeList([]); // Variable supprimée
    setShowDpeSuggestions(false);
    setIsLoadingDpe(false);
    setShowDpePopup(false);
    // setSelectedDpe(null); // Variable supprimée
    setPendingSubmission(false);
    setIsDpeSelectionInProgress(false);
    resetDpePagination();
    
    // Réinitialiser les états de soumission
    setIsSubmitting(false);
    setShowConfirmationModal(false);
    
    // Vider la session storage
    sessionStorage.removeItem('hectorSession');
    
    console.log('✅ Toutes les données ont été nettoyées');
  };

  // Fonction pour fermer la modal avec nettoyage des données
  const handleModalClose = () => {
    clearAllData();
    onClose();
  };

  // Fonction pour valider l'adresse avec l'API
  const validateAddress = async (address: string): Promise<boolean> => {
    if (!address.trim()) {
      setAddressValidationError('L\'adresse est requise');
      return false;
    }

    setIsValidatingAddress(true);
    setAddressValidationError('');

    try {
      const addressValidationResponse = await fetch(
        `https://api-adresse.data.gouv.fr/search/?q=${encodeURIComponent(address)}&limit=1`
      );
      const addressValidationData = await addressValidationResponse.json();

      // Vérifier si l'adresse a été trouvée
      if (!addressValidationData.features || addressValidationData.features.length === 0) {
        setAddressValidationError('L\'adresse que vous avez saisie n\'a pas été trouvée. Veuillez saisir de nouveau une adresse valide');
        setIsValidatingAddress(false);
        return false;
      }

      setIsValidatingAddress(false);
      return true;
    } catch (addressError) {
      console.error('Erreur lors de la validation de l\'adresse:', addressError);
      setAddressValidationError('Erreur lors de la validation de l\'adresse. Veuillez réessayer.');
      setIsValidatingAddress(false);
      return false;
    }
  };

  const validateField = externalValidateField || ((fieldName: keyof ContactFormData, value: any) => {
    let error = '';

    switch (fieldName) {
      case 'prenom':
        if (!value || !value.trim()) {
          error = 'Le prénom est requis';
        }
        break;
      case 'nom':
        if (!value || !value.trim()) {
          error = 'Le nom est requis';
        }
        break;
      case 'mail':
        if (value && !validateEmail(value)) {
          error = 'Format d\'email invalide';
        }
        break;
      case 'telephone':
        if (value && !validateTelephone(value)) {
          error = 'Format de numéro de téléphone invalide (ex: 0123456789, 01 23 45 67 89)';
        }
        break;
      case 'professionnelsRge':
        if (!value) {
          error = 'Vous devez accepter d\'être mis en relation avec des professionnels RGE';
        }
        break;
      case 'conditionsGenerales':
        if (!value) {
          error = 'Vous devez accepter les conditions générales';
        }
        break;
    }

    setErrors(prev => ({
      ...prev,
      [fieldName]: error
    }));
  });

  const validateForm = externalValidateForm || ((): boolean => {
    const newErrors: Partial<Record<keyof ContactFormData, string>> = {};
    
    // Validation nom et prénom
    if (!formData.nom.trim()) newErrors.nom = 'Le nom est requis';
    if (!formData.prenom.trim()) newErrors.prenom = 'Le prénom est requis';
    
    // Validation email
    if (!formData.mail.trim()) {
      newErrors.mail = 'L\'email est requis';
    } else if (!validateEmail(formData.mail)) {
      newErrors.mail = 'Format d\'email invalide';
    }
    
    // Validation adresse (DPE optionnel)
    if (!formData.adresse.trim()) newErrors.adresse = 'L\'adresse est requise';
    
    // Validation téléphone
    if (!formData.telephone.trim()) {
      newErrors.telephone = 'Le téléphone est requis';
    } else if (!validateTelephone(formData.telephone)) {
      newErrors.telephone = 'Format de numéro de téléphone invalide (ex: 0123456789, 01 23 45 67 89)';
    }
    
    // Validation professionnels RGE
    if (!formData.professionnelsRge) {
      newErrors.professionnelsRge = 'Vous devez accepter d\'être mis en relation avec des professionnels RGE';
    }
    
    // Validation conditions générales
    if (!formData.conditionsGenerales) {
      newErrors.conditionsGenerales = 'Vous devez accepter les conditions générales';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    console.log('handleSubmit appelé, formData actuel:', formData);
    console.log('numeroDpe actuel:', formData.numeroDpe);
    
    // Marquer tous les champs comme touchés pour afficher les erreurs
    const allFields: (keyof ContactFormData)[] = ['nom', 'prenom', 'mail', 'adresse', 'numeroDpe', 'telephone', 'professionnelsRge', 'conditionsGenerales'];
    const touchedFields = allFields.reduce((acc, field) => ({ ...acc, [field]: true }), {});
    setTouched(prev => ({ ...prev, ...touchedFields }));
    
    // Valider le formulaire de base
    if (!validateForm()) {
      return;
    }

    // Valider l'adresse avec l'API
    const isAddressValid = await validateAddress(formData.adresse);
    if (!isAddressValid) {
      return;
    }
    
    // Marquer qu'une soumission est en attente
    setPendingSubmission(true);
    
    // L'useEffect gérera l'ouverture du popup ou la soumission directe
    // selon l'état des DPE (chargés, en cours, ou non trouvés)
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Modal de contact */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-2 sm:p-4">
        {/* Backdrop */}
        <div 
          className="absolute inset-0 bg-black bg-opacity-50 animate-fade-in"
          onClick={handleModalClose}
        />
        
        {/* Modal */}
        <div className="relative bg-white rounded-2xl sm:rounded-3xl shadow-2xl max-w-2xl lg:max-w-4xl xl:max-w-5xl w-full mx-2 sm:mx-4 lg:mx-8 animate-slide-up overflow-hidden max-h-[95vh] sm:max-h-none overflow-y-auto sm:overflow-y-visible">
        {/* Header with Dynamic Color */}
        <div 
          className={`relative p-4 sm:p-6 lg:p-8 ${buttonColor === '#f5f5dc' || buttonColor === '#c0c0c0' ? 'text-gray-800' : 'text-white'}`}
          style={{ 
            background: buttonColor === '#f5f5dc' || buttonColor === '#c0c0c0' 
              ? `linear-gradient(135deg, ${buttonColor}, ${buttonColor}dd)` 
              : `linear-gradient(135deg, ${buttonColor}, ${buttonColor}cc)`
          }}
        >
          <div className="absolute inset-0 bg-black bg-opacity-10"></div>
            <div className="relative flex items-center justify-between">
              <div className="flex items-center space-x-2 sm:space-x-3 lg:space-x-4">
                <div className={`w-10 h-10 sm:w-12 sm:h-12 lg:w-14 lg:h-14 rounded-full flex items-center justify-center ${
                  buttonColor === '#f5f5dc' || buttonColor === '#c0c0c0' 
                    ? 'bg-gray-800 bg-opacity-20' 
                    : 'bg-white bg-opacity-20'
                }`}>
                  <svg className="w-5 h-5 sm:w-6 sm:h-6 lg:w-7 lg:h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                </svg>
              </div>
              <div>
                <h2 className="text-lg sm:text-xl lg:text-2xl font-bold">
                  Me faire appeler par un expert
                </h2>
                <p className={`text-xs sm:text-sm lg:text-base ${
                  buttonColor === '#f5f5dc' || buttonColor === '#c0c0c0' 
                    ? 'text-gray-600' 
                    : 'text-blue-100'
                }`}>
                  Remplissez le formulaire ci-dessous
                </p>
              </div>
            </div>
            <button
              onClick={handleModalClose}
              className={`transition-colors p-1.5 sm:p-2 lg:p-3 rounded-full ${
                buttonColor === '#f5f5dc' || buttonColor === '#c0c0c0'
                  ? 'text-gray-800 hover:text-gray-600 hover:bg-gray-800 hover:bg-opacity-20'
                  : 'text-white hover:text-gray-200 hover:bg-white hover:bg-opacity-20'
              }`}
              aria-label="Fermer"
              data-hector-widget-close="true"
            >
              <svg className="w-4 h-4 sm:w-5 sm:h-5 lg:w-6 lg:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-4 sm:p-8 lg:p-10 space-y-3 sm:space-y-4 lg:space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
            {/* Nom */}
            <div>
              <label htmlFor="nom" className="block text-xs sm:text-sm lg:text-base font-semibold text-gray-800 mb-1 sm:mb-2">
                Nom *
              </label>
              <input
                type="text"
                id="nom"
                name="nom"
                value={formData.nom}
                onChange={handleInputChange}
                className={`w-full px-3 sm:px-4 lg:px-5 py-2 sm:py-2.5 lg:py-3 border-2 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 bg-gray-50 focus:bg-white text-sm sm:text-base lg:text-lg ${
                  touched.nom && errors.nom ? 'border-red-400 bg-red-50' : 'border-gray-200'
                }`}
                placeholder="Votre nom"
              />
              {touched.nom && errors.nom && <p className="text-red-500 text-xs mt-1 font-medium">{errors.nom}</p>}
            </div>

            {/* Prénom */}
            <div>
              <label htmlFor="prenom" className="block text-xs sm:text-sm lg:text-base font-semibold text-gray-800 mb-1 sm:mb-2">
                Prénom *
              </label>
              <input
                type="text"
                id="prenom"
                name="prenom"
                value={formData.prenom}
                onChange={handleInputChange}
                className={`w-full px-3 sm:px-4 lg:px-5 py-2 sm:py-2.5 lg:py-3 border-2 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 bg-gray-50 focus:bg-white text-sm sm:text-base lg:text-lg ${
                  touched.prenom && errors.prenom ? 'border-red-400 bg-red-50' : 'border-gray-200'
                }`}
                placeholder="Votre prénom"
              />
              {touched.prenom && errors.prenom && <p className="text-red-500 text-xs mt-1 font-medium">{errors.prenom}</p>}
            </div>
          </div>

          {/* Email */}
          <div>
              <label htmlFor="mail" className="block text-xs sm:text-sm lg:text-base font-semibold text-gray-800 mb-1 sm:mb-2">
                Email *
              </label>
              <input
                type="email"
                id="mail"
                name="mail"
                value={formData.mail}
                onChange={handleInputChange}
                className={`w-full px-3 sm:px-4 lg:px-5 py-2 sm:py-2.5 lg:py-3 border-2 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 bg-gray-50 focus:bg-white text-sm sm:text-base lg:text-lg ${
                  touched.mail && errors.mail ? 'border-red-400 bg-red-50' : 'border-gray-200'
                }`}
              placeholder="votre@email.com"
            />
            {touched.mail && errors.mail && <p className="text-red-500 text-xs mt-1 font-medium">{errors.mail}</p>}
          </div>

          {/* Adresse */}
          <div className="relative address-suggestions-container">
              <label htmlFor="adresse" className="block text-xs sm:text-sm lg:text-base font-semibold text-gray-800 mb-1 sm:mb-2">
                Adresse Postale du bien immobilier *
              </label>
              <input
                type="text"
                id="adresse"
                name="adresse"
                value={formData.adresse}
                onChange={handleInputChange}
                className={`w-full px-3 sm:px-4 lg:px-5 py-2 sm:py-2.5 lg:py-3 border-2 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 bg-gray-50 focus:bg-white text-sm sm:text-base lg:text-lg ${
                  touched.adresse && errors.adresse ? 'border-red-400 bg-red-50' : 'border-gray-200'
                }`}
              placeholder="Commencez à taper votre adresse..."
              autoComplete="off"
            />
            {touched.adresse && errors.adresse && <p className="text-red-500 text-xs mt-1 font-medium">{errors.adresse}</p>}
            {addressValidationError && <p className="text-red-500 text-xs mt-1 font-medium">{addressValidationError}</p>}
            
            {/* Liste des suggestions d'adresse */}
            {showSuggestions && suggestions.length > 0 && (
              <div className="absolute z-20 w-full bg-white border-2 border-gray-200 rounded-xl shadow-lg max-h-60 overflow-y-auto mt-1">
                {suggestions.map((suggestion, index) => (
                  <div
                    key={index}
                    className="p-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0 transition-colors"
                    onClick={() => handleAddressSelect(suggestion)}
                  >
                    <div className="font-medium text-gray-900">
                      {suggestion.housenumber} {suggestion.street}
                    </div>
                    <div className="text-sm text-gray-600">
                      {suggestion.postcode} {suggestion.city}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>


          {/* Téléphone */}
          <div>
              <label htmlFor="telephone" className="block text-xs sm:text-sm lg:text-base font-semibold text-gray-800 mb-1 sm:mb-2">
                Téléphone *
              </label>
              <div className="flex">
                {/* Sélecteur de pays */}
                 <select
                   name="paysCode"
                   value={formData.paysCode}
                   onChange={handleInputChange}
                   className={`px-2 sm:px-3 lg:px-4 py-2 sm:py-2.5 lg:py-3 border-2 border-r-0 rounded-l-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 bg-gray-50 focus:bg-white text-xs sm:text-sm lg:text-base w-20 sm:w-24 lg:w-28 ${
                     errors.telephone ? 'border-red-400 bg-red-50' : 'border-gray-200'
                   }`}
                 >
                  {countries.map((country) => (
                    <option key={country.code + country.name} value={country.code}>
                      {country.flag} {country.code}
                    </option>
                  ))}
                </select>
                
                {/* Champ téléphone */}
                <input
                  type="tel"
                  id="telephone"
                  name="telephone"
                  value={formData.telephone}
                  onChange={handleInputChange}
                  className={`flex-1 px-3 sm:px-4 lg:px-5 py-2 sm:py-2.5 lg:py-3 border-2 rounded-r-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 bg-gray-50 focus:bg-white text-sm sm:text-base lg:text-lg ${
                    touched.telephone && errors.telephone ? 'border-red-400 bg-red-50' : 'border-gray-200'
                  }`}
                placeholder="0123456789 ou 01 23 45 67 89"
              />
            </div>
                    {touched.telephone && errors.telephone && <p className="text-red-500 text-xs mt-1 font-medium">{errors.telephone}</p>}
          </div>

          {/* Toggle Switch pour Professionnels RGE */}
            <div className={`border-2 border-dashed rounded-xl p-2 sm:p-3 lg:p-4 ${
              touched.professionnelsRge && errors.professionnelsRge 
                ? 'border-red-200 bg-red-50' 
                : 'border-green-200 bg-green-50'
            }`}>
              <div className="flex items-start space-x-2 sm:space-x-3 lg:space-x-4">
                {/* Toggle Switch */}
                <div className="flex-shrink-0 mt-1">
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      name="professionnelsRge"
                      checked={formData.professionnelsRge}
                      onChange={handleInputChange}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-green-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-500"></div>
                  </label>
                </div>
                
                {/* Texte */}
                <div className="flex-1">
                  <p className="text-xs sm:text-sm lg:text-base font-medium text-gray-900 mb-1">
                    Je veux être mis en relation avec des professionnels certifiés RGE pour recevoir des devis sans engagement
                  </p>
                  <p className="text-xs sm:text-sm lg:text-base text-gray-600 leading-relaxed">
                    Nous sélectionnons pour vous les professionnels certifiés RGE, proches de chez vous, les mieux habilités pour vous accompagner dans votre projet. Cette option ne vous engage à aucun achat ni souscription de votre part.
                  </p>
                {touched.professionnelsRge && errors.professionnelsRge && (
                  <p className="text-red-500 text-xs mt-2 font-medium">{errors.professionnelsRge}</p>
                )}
              </div>
            </div>
          </div>

          {/* Checkbox Conditions Générales */}
            <div className="flex items-start space-x-2 sm:space-x-3 lg:space-x-4">
              <div className="flex-shrink-0 mt-1">
                <input
                  type="checkbox"
                  id="conditionsGenerales"
                  name="conditionsGenerales"
                  checked={formData.conditionsGenerales}
                  onChange={handleInputChange}
                  className={`w-4 h-4 lg:w-5 lg:h-5 rounded border-2 focus:ring-2 focus:ring-offset-0 transition-colors ${
                    touched.conditionsGenerales && errors.conditionsGenerales 
                      ? 'border-red-400 focus:ring-red-500' 
                      : 'border-gray-300 focus:ring-blue-500'
                  }`}
                />
              </div>
              <div className="flex-1">
                <label htmlFor="conditionsGenerales" className="text-xs sm:text-sm lg:text-base text-gray-700 leading-relaxed">
                En cochant cette case, vous reconnaissez avoir pris connaissance de la{' '}
                <a href="/politique-confidentialite" className="text-green-600 hover:text-green-700 underline">Politique de Confidentialité</a>
                {' '}et des{' '}
                <a href="/conditions-generales" className="text-green-600 hover:text-green-700 underline">Conditions Générales</a>.
              </label>
                    {touched.conditionsGenerales && errors.conditionsGenerales && (
                      <p className="text-red-500 text-xs mt-1 font-medium">{errors.conditionsGenerales}</p>
                    )}
            </div>
          </div>

          {/* Submit Button */}
          <div className="pt-2">
                  <button
                    type="submit"
                    disabled={isValidatingAddress || isSubmitting}
                    className={`w-full font-bold py-2.5 sm:py-3 lg:py-4 px-4 sm:px-6 lg:px-8 rounded-xl transition-all duration-200 transform hover:scale-105 focus:ring-2 focus:ring-offset-2 shadow-lg hover:shadow-xl text-sm sm:text-base lg:text-lg ${
                      isValidatingAddress || isSubmitting
                        ? 'opacity-75 cursor-not-allowed' 
                        : ''
                    } ${
                      buttonColor === '#f5f5dc' || buttonColor === '#c0c0c0'
                        ? 'text-gray-800 focus:ring-gray-500'
                        : 'text-white focus:ring-blue-500'
                    }`}
              style={{
                background: buttonColor === '#f5f5dc' || buttonColor === '#c0c0c0'
                  ? `linear-gradient(135deg, ${buttonColor}, ${buttonColor}dd)`
                  : `linear-gradient(135deg, ${buttonColor}, ${buttonColor}cc)`
              }}
              onMouseEnter={(e) => {
                if (isValidatingAddress) return;
                const isLightColor = buttonColor === '#f5f5dc' || buttonColor === '#c0c0c0';
                if (isLightColor) {
                  e.currentTarget.style.background = buttonColor === '#f5f5dc' ? 'linear-gradient(135deg, #e6e6c7, #d4d4a8)' : 'linear-gradient(135deg, #a8a8a8, #909090)';
                } else {
                  e.currentTarget.style.background = `linear-gradient(135deg, ${buttonColor}dd, ${buttonColor}bb)`;
                }
              }}
              onMouseLeave={(e) => {
                if (isValidatingAddress) return;
                const isLightColor = buttonColor === '#f5f5dc' || buttonColor === '#c0c0c0';
                if (isLightColor) {
                  e.currentTarget.style.background = `linear-gradient(135deg, ${buttonColor}, ${buttonColor}dd)`;
                } else {
                  e.currentTarget.style.background = `linear-gradient(135deg, ${buttonColor}, ${buttonColor}cc)`;
                }
              }}
            >
                    <div className="flex items-center justify-center space-x-2">
                      {isValidatingAddress ? (
                        <>
                          <div className="w-4 h-4 sm:w-5 sm:h-5 lg:w-6 lg:h-6 border-2 border-current border-t-transparent rounded-full animate-spin"></div>
                          <span className="text-xs sm:text-sm lg:text-base">Validation de l'adresse...</span>
                        </>
                      ) : isSubmitting ? (
                        <>
                          <div className="w-4 h-4 sm:w-5 sm:h-5 lg:w-6 lg:h-6 border-2 border-current border-t-transparent rounded-full animate-spin"></div>
                          <span className="text-xs sm:text-sm lg:text-base">Envoi en cours...</span>
                        </>
                      ) : (
                        <>
                          <svg className="w-4 h-4 sm:w-5 sm:h-5 lg:w-6 lg:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                          </svg>
                          <span className="text-xs sm:text-sm lg:text-base">Envoyer ma demande d'appel</span>
                        </>
                      )}
                    </div>
            </button>
          </div>
        </form>
      </div>
    </div>

    {/* Popup de sélection DPE */}
    {showDpePopup && (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-2 sm:p-4">
        {/* Backdrop */}
        <div 
          className="absolute inset-0 bg-black bg-opacity-50 animate-fade-in"
          onClick={handleContinueWithoutDpe}
        />
        
        {/* Popup DPE */}
        <div className="relative bg-white rounded-2xl sm:rounded-3xl shadow-2xl max-w-2xl lg:max-w-4xl xl:max-w-5xl w-full mx-2 sm:mx-4 lg:mx-8 animate-slide-up overflow-hidden max-h-[90vh] sm:max-h-[80vh] overflow-y-auto">
          {/* Header */}
          <div 
            className={`relative p-4 sm:p-6 lg:p-8 ${buttonColor === '#f5f5dc' || buttonColor === '#c0c0c0' ? 'text-gray-800' : 'text-white'}`}
            style={{ 
              background: buttonColor === '#f5f5dc' || buttonColor === '#c0c0c0' 
                ? `linear-gradient(135deg, ${buttonColor}, ${buttonColor}dd)` 
                : `linear-gradient(135deg, ${buttonColor}, ${buttonColor}cc)`
            }}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2 sm:space-x-3">
                <svg className="w-6 h-6 sm:w-8 sm:h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
                <div>
                  <h2 className="text-lg sm:text-xl font-bold">
                    Sélectionnez votre DPE
                  </h2>
                  <p className={`text-xs sm:text-sm ${
                    buttonColor === '#f5f5dc' || buttonColor === '#c0c0c0' 
                      ? 'text-gray-600' 
                      : 'text-blue-100'
                  }`}>
                    {dpeList.length > 0 ? `${dpeList.length} DPE trouvé(s) pour cette adresse` : 'Aucun DPE trouvé pour cette adresse'}
                  </p>
                </div>
              </div>
              <button
                onClick={handleContinueWithoutDpe}
                className={`transition-colors p-1.5 sm:p-2 rounded-full ${
                  buttonColor === '#f5f5dc' || buttonColor === '#c0c0c0'
                    ? 'text-gray-800 hover:text-gray-600 hover:bg-gray-800 hover:bg-opacity-20'
                    : 'text-white hover:text-gray-200 hover:bg-white hover:bg-opacity-20'
                }`}
                aria-label="Fermer"
              >
                <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="p-3 sm:p-6">
            {dpeList.length > 0 ? (
              <>
                <div className="space-y-2 sm:space-y-3">
                  {getCurrentPageDpes().map((dpe, index) => {
                    const dpeColors = getDpeClassColor(dpe.classe);
                    return (
                      <div
                        key={index}
                        className="p-3 sm:p-4 border-2 border-gray-200 rounded-xl hover:border-blue-300 cursor-pointer transition-colors"
                        onClick={() => handleDpeSelect(dpe)}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center space-x-2 sm:space-x-3 mb-1">
                              <div className="font-medium text-gray-900 text-sm sm:text-base truncate">
                                {dpe.numeroDpe}
                              </div>
                              {/* Badge classe DPE coloré */}
                              <div className={`px-2 py-1 rounded-lg text-xs font-bold ${dpeColors.bg} ${dpeColors.text} border ${dpeColors.border}`}>
                                Classe {dpe.classe}
                              </div>
                            </div>
                            <div className="text-xs sm:text-sm text-gray-600 mt-1 break-words">
                              <span className="block sm:inline">Type bâtiment: {dpe.typeBatiment}</span>
                              <span className="hidden sm:inline"> | </span>
                              <span className="block sm:inline">Surface: {dpe.surfaceHabitation}</span>
                              <span className="hidden sm:inline"> | </span>
                              <span className="block sm:inline">Énergies: {dpe.energies}</span>
                            </div>
                            <div className="text-xs text-gray-500 mt-1">
                              Date: {dpe.dateRealisation ? new Date(dpe.dateRealisation).toLocaleDateString('fr-FR') : 'N/A'}
                            </div>
                          </div>
                          <div className="flex-shrink-0 ml-2">
                            <svg className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Pagination */}
                {getTotalPages() > 1 && (
                  <div className="mt-4 sm:mt-6 pt-3 sm:pt-4 border-t border-gray-200">
                    <div className="flex items-center justify-between">
                      {/* Info pagination */}
                      <div className="text-xs sm:text-sm text-gray-600">
                        Page {currentDpePage} sur {getTotalPages()} 
                        <span className="hidden sm:inline"> ({dpeList.length} DPE au total)</span>
                      </div>

                      {/* Contrôles de pagination */}
                      <div className="flex items-center space-x-1 sm:space-x-2">
                        {/* Bouton Précédent */}
                        <button
                          onClick={() => handleDpePageChange(currentDpePage - 1)}
                          disabled={currentDpePage === 1}
                          className={`p-1.5 sm:p-2 rounded-lg transition-colors ${
                            currentDpePage === 1
                              ? 'text-gray-300 cursor-not-allowed'
                              : 'text-gray-600 hover:text-gray-800 hover:bg-gray-100'
                          }`}
                          aria-label="Page précédente"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                          </svg>
                        </button>

                        {/* Numéros de page */}
                        <div className="flex items-center space-x-1">
                          {Array.from({ length: getTotalPages() }, (_, i) => i + 1).map((page) => (
                            <button
                              key={page}
                              onClick={() => handleDpePageChange(page)}
                              className={`px-2 sm:px-3 py-1 sm:py-1.5 text-xs sm:text-sm rounded-lg transition-colors ${
                                page === currentDpePage
                                  ? 'bg-blue-500 text-white'
                                  : 'text-gray-600 hover:text-gray-800 hover:bg-gray-100'
                              }`}
                            >
                              {page}
                            </button>
                          ))}
                        </div>

                        {/* Bouton Suivant */}
                        <button
                          onClick={() => handleDpePageChange(currentDpePage + 1)}
                          disabled={currentDpePage === getTotalPages()}
                          className={`p-1.5 sm:p-2 rounded-lg transition-colors ${
                            currentDpePage === getTotalPages()
                              ? 'text-gray-300 cursor-not-allowed'
                              : 'text-gray-600 hover:text-gray-800 hover:bg-gray-100'
                          }`}
                          aria-label="Page suivante"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-6 sm:py-8">
                <svg className="w-10 h-10 sm:w-12 sm:h-12 text-gray-400 mx-auto mb-3 sm:mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 12h6m-6-4h6m2 5.291A7.962 7.962 0 0112 15c-2.34 0-4.29-1.009-5.824-2.709M15 6.291A7.962 7.962 0 0012 5c-2.34 0-4.29 1.009-5.824 2.709" />
                </svg>
                <p className="text-sm sm:text-base text-gray-600 mb-3 sm:mb-4 px-4">Aucun DPE trouvé pour cette adresse</p>
              </div>
            )}

            {/* Bouton Continuer sans DPE */}
            <div className="mt-4 sm:mt-6 pt-3 sm:pt-4 border-t border-gray-200">
              <button
                onClick={handleContinueWithoutDpe}
                className={`w-full font-bold py-2.5 sm:py-3 px-3 sm:px-6 rounded-xl transition-all duration-200 transform hover:scale-105 focus:ring-2 focus:ring-offset-2 shadow-lg hover:shadow-xl text-xs sm:text-sm ${
                  buttonColor === '#f5f5dc' || buttonColor === '#c0c0c0'
                    ? 'text-gray-800 focus:ring-gray-500 bg-gray-200 hover:bg-gray-300'
                    : 'text-white focus:ring-blue-500'
                }`}
                style={{
                  background: buttonColor === '#f5f5dc' || buttonColor === '#c0c0c0'
                    ? '#e5e7eb'
                    : `linear-gradient(135deg, ${buttonColor}, ${buttonColor}cc)`
                }}
              >
                Continuer sans DPE
              </button>
            </div>
          </div>
        </div>
      </div>
    )}

    {/* Modal de confirmation */}
    <ConfirmationModal
      isOpen={showConfirmationModal}
      onClose={() => {
        setShowConfirmationModal(false);
        onClose(); // Fermer aussi la ContactModal
      }}
      buttonColor={buttonColor}
    />
    </>
  );
};

// Composant pour la modal de confirmation
const ConfirmationModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  buttonColor?: string;
}> = ({ isOpen, onClose, buttonColor = '#1a1a1a' }) => {
  useEffect(() => {
    if (isOpen) {
      // Fermer automatiquement après 1 minute
      const timer = setTimeout(() => {
        onClose();
      }, 60000); // 60 secondes

      return () => clearTimeout(timer);
    }
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-2 sm:p-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black bg-opacity-50 animate-fade-in"
        onClick={onClose}
      />
      
        {/* Modal */}
        <div className="relative bg-white rounded-2xl sm:rounded-3xl shadow-2xl max-w-md lg:max-w-lg xl:max-w-xl w-full mx-2 sm:mx-4 lg:mx-8 animate-slide-up overflow-hidden">
        {/* Header */}
        <div 
          className={`relative p-4 sm:p-6 lg:p-8 ${buttonColor === '#f5f5dc' || buttonColor === '#c0c0c0' ? 'text-gray-800' : 'text-white'}`}
          style={{ 
            background: buttonColor === '#f5f5dc' || buttonColor === '#c0c0c0' 
              ? `linear-gradient(135deg, ${buttonColor}, ${buttonColor}dd)` 
              : `linear-gradient(135deg, ${buttonColor}, ${buttonColor}cc)`
          }}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2 sm:space-x-3">
              <svg className="w-6 h-6 sm:w-8 sm:h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div>
                <h2 className="text-lg sm:text-xl font-bold">
                  Demande envoyée !
                </h2>
                <p className={`text-xs sm:text-sm ${
                  buttonColor === '#f5f5dc' || buttonColor === '#c0c0c0' 
                    ? 'text-gray-600' 
                    : 'text-blue-100'
                }`}>
                  Votre appel va arriver dans quelques minutes
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className={`transition-colors p-1.5 sm:p-2 rounded-full ${
                buttonColor === '#f5f5dc' || buttonColor === '#c0c0c0'
                  ? 'text-gray-800 hover:text-gray-600 hover:bg-gray-800 hover:bg-opacity-20'
                  : 'text-white hover:text-gray-200 hover:bg-white hover:bg-opacity-20'
              }`}
              aria-label="Fermer"
            >
              <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-4 sm:p-6">
          <div className="text-center">
            <div className="w-12 h-12 sm:w-16 sm:h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4">
              <svg className="w-6 h-6 sm:w-8 sm:h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-2">
              Parfait ! Votre demande a été transmise
            </h3>
            <p className="text-sm sm:text-base text-gray-600 mb-4 sm:mb-6">
              Un expert Hector va vous appeler dans les prochaines minutes au numéro que vous avez fourni.
            </p>
            <button
              onClick={onClose}
              className={`w-full font-bold py-2.5 sm:py-3 px-4 sm:px-6 rounded-xl transition-all duration-200 transform hover:scale-105 focus:ring-2 focus:ring-offset-2 shadow-lg hover:shadow-xl text-sm sm:text-base ${
                buttonColor === '#f5f5dc' || buttonColor === '#c0c0c0'
                  ? 'text-gray-800 focus:ring-gray-500'
                  : 'text-white focus:ring-blue-500'
              }`}
              style={{
                background: buttonColor === '#f5f5dc' || buttonColor === '#c0c0c0'
                  ? `linear-gradient(135deg, ${buttonColor}, ${buttonColor}dd)`
                  : `linear-gradient(135deg, ${buttonColor}, ${buttonColor}cc)`
              }}
            >
              Compris, merci !
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactModal;
